import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Link2, Trash2, Play } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { mediaSrc } from "@/components/sections/Gallery";

const CATS = [["battle", "Battle"], ["spectacle", "Spectacle"], ["stage", "Stage"]];
const inputCls = "mt-1 w-full rounded-lg bg-ink border border-white/10 px-3 py-2.5 text-cream focus:border-mustard focus:outline-none";

export function GalleryManager({ cfg }) {
  const [items, setItems] = useState([]);
  const [photo, setPhoto] = useState({ title: "", category: "battle", file: null });
  const [video, setVideo] = useState({ title: "", category: "spectacle", url: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadPhoto = async () => {
    if (!photo.file) { toast.error("Choisis une image"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", photo.file); fd.append("title", photo.title); fd.append("category", photo.category);
    try {
      await api.post("/admin/gallery/photo", fd, { ...cfg, headers: { ...cfg.headers, "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée");
      setPhoto({ title: "", category: photo.category, file: null });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addVideo = async () => {
    if (!video.url || !video.title) { toast.error("Titre et lien requis"); return; }
    setBusy(true);
    try {
      await api.post("/admin/gallery/video", video, cfg);
      toast.success("Vidéo ajoutée");
      setVideo({ title: "", category: video.category, url: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Retirer ce média de la galerie ?")) return;
    try { await api.delete(`/admin/gallery/${id}`, cfg); toast.success("Média retiré"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-gallery-manager">
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-sub font-semibold mb-4 inline-flex items-center gap-2"><Upload className="w-4 h-4 text-mustard" /> Ajouter une photo</h3>
          <label className="block mb-3"><span className="text-xs text-cream/50">Titre</span>
            <input data-testid="gallery-photo-title" value={photo.title} onChange={(e) => setPhoto({ ...photo, title: e.target.value })} className={inputCls} placeholder="Ex: Finale du battle 2026" /></label>
          <label className="block mb-3"><span className="text-xs text-cream/50">Catégorie</span>
            <select data-testid="gallery-photo-category" value={photo.category} onChange={(e) => setPhoto({ ...photo, category: e.target.value })} className={inputCls}>
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="block mb-4"><span className="text-xs text-cream/50">Image (JPG, PNG, WEBP — 8 Mo max)</span>
            <input ref={fileRef} data-testid="gallery-photo-file" type="file" accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => setPhoto({ ...photo, file: e.target.files?.[0] || null })}
              className="mt-1 w-full text-sm text-cream/70 file:mr-3 file:rounded-full file:border-0 file:bg-mustard file:px-4 file:py-2 file:text-ink file:font-semibold" /></label>
          <button data-testid="gallery-photo-submit" onClick={uploadPhoto} disabled={busy} className="btn-mustard rounded-full px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
            <Upload className="w-4 h-4" /> {busy ? "Envoi…" : "Téléverser"}
          </button>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-sub font-semibold mb-4 inline-flex items-center gap-2"><Link2 className="w-4 h-4 text-mustard" /> Ajouter une vidéo</h3>
          <label className="block mb-3"><span className="text-xs text-cream/50">Titre</span>
            <input data-testid="gallery-video-title" value={video.title} onChange={(e) => setVideo({ ...video, title: e.target.value })} className={inputCls} placeholder="Ex: Spectacle Block Party" /></label>
          <label className="block mb-3"><span className="text-xs text-cream/50">Catégorie</span>
            <select data-testid="gallery-video-category" value={video.category} onChange={(e) => setVideo({ ...video, category: e.target.value })} className={inputCls}>
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="block mb-4"><span className="text-xs text-cream/50">Lien YouTube ou Vimeo</span>
            <input data-testid="gallery-video-url" value={video.url} onChange={(e) => setVideo({ ...video, url: e.target.value })} className={inputCls} placeholder="https://www.youtube.com/watch?v=…" /></label>
          <button data-testid="gallery-video-submit" onClick={addVideo} disabled={busy} className="btn-mustard rounded-full px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
            <Link2 className="w-4 h-4" /> Ajouter la vidéo
          </button>
        </div>
      </div>

      <div className="mt-8 glass rounded-2xl p-6">
        <h3 className="font-sub font-semibold mb-4">Médias publiés <span className="text-cream/40 font-mono2 text-sm">({items.length})</span></h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="admin-gallery-list">
          {items.map((it) => (
            <div key={it.id} data-testid={`admin-gallery-item-${it.id}`} className="group relative rounded-xl overflow-hidden border border-white/5 bg-ink aspect-square">
              <img src={mediaSrc(it)} alt={it.title} className="w-full h-full object-cover" />
              {it.type === "video" && <Play className="absolute top-2 left-2 w-5 h-5 text-mustard drop-shadow" />}
              <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink to-transparent">
                <p className="text-xs text-cream truncate">{it.title}</p>
                <p className="text-[10px] font-mono2 uppercase text-cream/50">{it.category}</p>
              </div>
              <button data-testid={`admin-gallery-delete-${it.id}`} onClick={() => remove(it.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ink/80 text-cream/70 hover:bg-destructive hover:text-white flex items-center justify-center transition">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {items.length === 0 && <p className="text-cream/40 text-sm font-mono2 col-span-full">Aucun média.</p>}
        </div>
      </div>
    </div>
  );
}
